import React, { useState, useEffect } from 'react';
import './App.css';
import authService from './services/authService';
import MicrosoftLogin from './components/MicrosoftLogin';
import Dashboard from './pages/Dashboard';
import CriticalAlertsPage from './pages/CriticalAlerts';
import CustomAlertRulesPage from './pages/CustomAlertRules';
import GatewayAlertsPage from './pages/GatewayAlerts';
import GatewaysPage from './pages/Gateways';
import TrialMachinesPage from './pages/TrialMachines';
import AnalyticsPage from './pages/Analytics';
import { ThemeProvider } from './contexts/ThemeContext';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    // Skip auth check on the SSO callback path — that useEffect handles it
    if (window.location.pathname === '/sso-callback') return;

    checkAuthStatus();

    // Set a timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      setIsLoading(false);
    }, 10000); // 10 seconds timeout

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    // Handle SSO callback: backend redirects here after Microsoft OAuth with token params
    if (window.location.pathname === '/sso-callback') {
      const token = urlParams.get('token');
      const refreshToken = urlParams.get('refresh_token');
      const expiresIn = urlParams.get('expires_in');
      const userJson = urlParams.get('user');
      const ssoError = urlParams.get('sso_error');

      if (ssoError) {
        console.error('SSO error:', ssoError);
        window.history.replaceState({}, document.title, '/');
        setIsLoading(false);
        return;
      }

      if (token && refreshToken && expiresIn) {
        localStorage.setItem('accessToken', token);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('tokenType', 'Bearer');
        localStorage.setItem('tokenExpiry', (Date.now() + parseInt(expiresIn) * 1000).toString());
        if (userJson) {
          try {
            localStorage.setItem('user', userJson);
          } catch (_) {}
        }
        setIsAuthenticated(true);
        setIsLoading(false);
        // Replace URL so the token params are not visible in history
        window.history.replaceState({}, document.title, '/dashboard');
        return;
      }
    }

    // Legacy: plain ?login=success param
    if (urlParams.get('login') === 'success') {
      setIsAuthenticated(true);
      setIsLoading(false);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const checkAuthStatus = async () => {
    try {
      const hasValidToken = authService.getAuthToken() && !authService.isTokenExpired();
      if (!hasValidToken) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }
      const authenticated = await authService.isAuthenticated();
      setIsAuthenticated(authenticated);
      if (!authenticated) {
        authService.clearAuth();
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      setIsAuthenticated(false);
      authService.clearAuth();
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  if (isLoading) {
    return (
      <ThemeProvider>
        <div className="App">
          <div className="loading" style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100vh',
            backgroundColor: 'var(--bg-primary)',
            color: 'var(--text-primary)'
          }}>
            <h2>Loading...</h2>
            <p>Checking authentication status...</p>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  // Handle routes for authenticated users
  if (isAuthenticated) {
    const currentPath = window.location.pathname;
    
    if (currentPath === '/critical-alerts') {
      return (
        <ThemeProvider>
          <div className="App">
            <CriticalAlertsPage />
          </div>
        </ThemeProvider>
      );
    }

    if (currentPath === '/gateway-alerts') {
      return (
        <ThemeProvider>
          <div className="App">
            <GatewayAlertsPage />
          </div>
        </ThemeProvider>
      );
    }
    
    if (currentPath === '/custom-alert-rules') {
      return (
        <ThemeProvider>
          <div className="App">
            <CustomAlertRulesPage />
          </div>
        </ThemeProvider>
      );
    }

    if (currentPath === '/gateways') {
      return (
        <ThemeProvider>
          <div className="App">
            <GatewaysPage />
          </div>
        </ThemeProvider>
      );
    }

    if (currentPath === '/trial-machines') {
      return (
        <ThemeProvider>
          <div className="App">
            <TrialMachinesPage />
          </div>
        </ThemeProvider>
      );
    }

    if (currentPath === '/analytics') {
      return (
        <ThemeProvider>
          <div className="App">
            <AnalyticsPage />
          </div>
        </ThemeProvider>
      );
    }
    
    // Default to dashboard for authenticated users
    return (
      <ThemeProvider>
        <div className="App">
          <Dashboard />
        </div>
      </ThemeProvider>
    );
  }

  // If not authenticated, show login
  return (
    <ThemeProvider>
      <div className="App">
        <MicrosoftLogin onLoginSuccess={handleLoginSuccess} />
      </div>
    </ThemeProvider>
  );
}

export default App;
